/**
 * Represents a numeric range closed on both ends. Can be unbounded and can be
 * empty.
 */
export abstract class ClosedNumericRange {
  /**
   * Determines if this range includes the given number.
   */
  abstract includes(n: number): boolean;
  /**
   * Returns the intersection of this range and the given other range.
   */
  abstract intersect(other: ClosedNumericRange): ClosedNumericRange;
  /**
   * Returns this range as a nonempty range, or undefined if this range is
   * empty.
   */
  abstract asNonemptyRange(): NonemptyClosedNumericRange | undefined;

  /**
   * Returns true if this range is the same as other. All empty ranges are the
   * same, and nonempty ranges are the same if and only if they have the same
   * bounds.
   */
  abstract equals(other: ClosedNumericRange): boolean;
}

/**
 * Represents a range with no elements. Use EmptyRange.VALUE rather than
 * constructing new instances for efficiency.
 */
export class EmptyRange extends ClosedNumericRange {
  static VALUE = new EmptyRange();

  includes() {
    return false;
  }

  intersect() {
    return this;
  }

  asNonemptyRange(): NonemptyClosedNumericRange | undefined {
    return undefined;
  }

  equals(other: ClosedNumericRange): boolean {
    return other === this;
  }
}

/**
 * Represents a non-empty range. Holds all values from min to max, inclusive on
 * both ends.
 */
export class NonemptyClosedNumericRange extends ClosedNumericRange {
  min: number;
  max: number;
  constructor(min: number | undefined, max: number | undefined) {
    super();
    this.min = min || Number.NEGATIVE_INFINITY;
    this.max = max || Number.POSITIVE_INFINITY;
  }

  includes(n: number): boolean {
    return this.min <= n && n <= this.max;
  }

  intersect(otherPossiblyEmpty: ClosedNumericRange): ClosedNumericRange {
    const other = otherPossiblyEmpty.asNonemptyRange();
    if (!other) {
      return EmptyRange.VALUE;
    }
    /* First if check handles:

       1.                    |-this-|
                   |-other-|
        (or 1' where this and other are flipped)
    */
    if (/* 1 */ other.max < this.min || /* 1' */ this.max < other.min) {
      return EmptyRange.VALUE;
    }
    /* All other cases:
       2.
                |-this-|
             |----other----|
       3.
            |---this---|
                |--other---|
      (and 2', 3' where this and other are flipped) are handled by taking the
      larger starting point and the smaller ending point. This is guaranteed to
      be nonempty because we've already handled the cases where they aren't
      above.
    */
    return range(Math.max(this.min, other.min), Math.min(this.max, other.max));
  }

  hasUnboundMax() {
    return this.max === Number.POSITIVE_INFINITY;
  }

  asNonemptyRange() {
    return this;
  }

  equals(otherPossiblyEmpty: ClosedNumericRange): boolean {
    const other = otherPossiblyEmpty.asNonemptyRange();
    if (!other) {
      return false;
    }
    return this.min === other.min && this.max === other.max;
  }
}

/**
 * Convenience function for constructing ranges.
 * @param lo smallest number in the range, or undefined for infinite extent
 * @param hi largest number in the range, or undefined for infinite extent
 */
export function range(lo: number | undefined, hi: number | undefined) {
  return new NonemptyClosedNumericRange(lo, hi);
}
